import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Instagram, Save, BadgeCheck } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import { IG_POST } from "./BeenTherePanel";

const IgPreview = ({ url }) => {
  const m = url.match(/instagram\.com\/(p|reel|tv)\/([\w-]+)/);
  return m && <iframe data-testid="founder-ig-preview" loading="lazy" title="Instagram post" src={`https://www.instagram.com/${m[1]}/${m[2]}/embed/captioned/`} className="mt-2 w-full max-w-[360px] h-[480px] rounded border border-stone-200 bg-white" />;
};

export const FounderLinksPanel = ({ kit, reload }) => {
  const places = useMemo(() => (kit.sections || []).flatMap((s) => (s.entries || []).filter((e) => e.name).map((e) => ({ section: s.title, name: e.name, instagram: e.instagram || "", visited: !!e.founder_verified }))), [kit.sections]);
  const [links, setLinks] = useState(() => Object.fromEntries(places.map((p) => [p.name, p.instagram])));
  const [visited, setVisited] = useState(() => Object.fromEntries(places.map((p) => [p.name, p.visited])));
  const [busy, setBusy] = useState(false);
  if (!places.length) return null;
  const nVisited = places.filter((p) => visited[p.name]).length;
  const setAll = (on) => setVisited(Object.fromEntries(places.map((p) => [p.name, on])));
  const bad = Object.values(links).filter((v) => v.trim() && !IG_POST.test(v.trim())).length;
  const linked = Object.values(links).filter((v) => IG_POST.test(v.trim())).length;
  const save = async () => {
    setBusy(true);
    try { const { data } = await http.put(`/kits/${kit.id}/founder-links`, { links, visited }); toast.success(data.updated ? `Saved — ${data.updated} change${data.updated === 1 ? "" : "s"} to your ✦ marks and links. The PDF is being rebuilt.` : "No changes to save"); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const groups = places.reduce((g, p) => ({ ...g, [p.section]: [...(g[p.section] || []), p] }), {});
  return (
    <div data-testid="founder-links-panel" className="rounded-xl border border-[#C9A847]/60 bg-[#FBF8F0] p-6 mb-6">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <div className="eyebrow flex items-center gap-2 text-[#9A7B2C]"><BadgeCheck size={12} /> Founder visits</div>
          <p data-testid="founder-links-count" className="text-sm text-stone-600 mt-1"><b data-testid="founder-visited-count">{nVisited}</b> of {places.length} places marked “I’ve been here” · {linked} with an Instagram post. Ticked places get the ✦ Founder Verified stamp in the PDF. No AI credit used.</p>
          <div className="flex gap-3 mt-2 text-xs">
            <button data-testid="founder-mark-all" onClick={() => setAll(true)} className="underline text-[#9A7B2C] hover:text-[#2B2A26]">Tick all</button>
            <button data-testid="founder-clear-all" onClick={() => setAll(false)} className="underline text-stone-500 hover:text-[#2B2A26]">Untick all</button>
          </div>
        </div>
        <button data-testid="founder-links-save" onClick={save} disabled={busy || bad > 0} className="ml-auto inline-flex items-center gap-2 rounded-sm bg-[#2B2A26] px-4 py-2 text-sm font-bold text-white disabled:opacity-50"><Save size={14} /> {bad ? `${bad} link${bad === 1 ? "" : "s"} to fix` : busy ? "Saving…" : "Save"}</button>
      </div>
      <div className="mt-4 grid gap-5 md:grid-cols-2">
        {Object.entries(groups).map(([sec, list]) => (
          <div key={sec}>
            <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500 mb-2">{sec}</div>
            {list.map((p, i) => {
              const v = links[p.name] || "";
              const wrong = v.trim() && !IG_POST.test(v.trim());
              const on = visited[p.name];
              const tid = `${sec}-${i}`.replace(/\s+/g, "-").toLowerCase();
              return (
                <div key={p.name} className="block mb-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" data-testid={`founder-visited-${tid}`} checked={!!on} onChange={(e) => setVisited({ ...visited, [p.name]: e.target.checked })} className="h-4 w-4 accent-[#B08E3A]" />
                    <span className={`text-sm font-semibold ${on ? "text-[#2B2A26]" : "text-stone-400"}`}>{on ? "✦ " : ""}{p.name}</span>
                  </label>
                  {on && <><div className="mt-1 flex items-center gap-2 rounded-sm border bg-white px-2" style={{ borderColor: !v ? "#D6D3D1" : wrong ? "#F59E0B" : "#10B981" }}>
                    <Instagram size={14} className={!v ? "text-stone-400" : wrong ? "text-amber-600" : "text-emerald-600"} />
                    <input data-testid={`founder-link-input-${sec}-${i}`.replace(/\s+/g, "-").toLowerCase()} value={v} onChange={(e) => setLinks({ ...links, [p.name]: e.target.value })} placeholder="https://www.instagram.com/p/…" className="h-9 w-full bg-transparent text-sm outline-none" />
                  </div>
                  {IG_POST.test(v.trim()) && <IgPreview url={v.trim()} />}</>}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};
